import React, { useCallback, useEffect, useState, useContext } from 'react';
import styled from 'styled-components';
import _ from 'lodash';

import { useTranslation } from '@com/core/common/translations/i18n';
import { ToastContextProvider } from '@com/core/common/contexts/toastContext';
import { customerSVC } from '@com/core/common/services/api';
import { AppContext } from '@com/core/common/contexts/appContext';
import { CustomerContext } from '@com/core/common/contexts/customerContext';
import { DailyContext } from '@com/core/common/contexts/dailyContext';
import { MapContext, MapContextProvider } from '@com/core/common/contexts/mapContext';
import { TAddress } from '@com/core/common/types/customer_address';
import { getMarketplaceConfig } from '@com/core/common/utils/config';
import { MARKETPLACE } from '@com/core/common/baseConfig';
import Map from './map/Map';
import AccountLayout from '../common/AccountLayout';
import BlockButton from '../common/BlockButton';
import Loader from '../common/Loader';

import AddressList from './AddressList';
import AddressForm from './AddressForm';

type TAddressViews = 'list' | 'form' | 'mapBrowse' | 'loading';
type TAddressFormRoles = 'add' | 'edit';

const AddressMain = (): JSX.Element => {
    const { t } = useTranslation('common');
    const { locale } = useContext(AppContext);
    const { browsingAddress, changeCanEnterDaily, setBrowsingAddress } = useContext(DailyContext);

    const { addresses, customer, isCustomerLoading } = useContext(CustomerContext);
    const {
        clickedLocation,
        handleMapClickDaily,
        removeClickedLocation,
        setClickedLocation,
        updateSearchText,
    } = useContext(MapContext);

    const config = getMarketplaceConfig(MARKETPLACE);

    const [addressView, setAddressView] = useState<TAddressViews>('loading');
    const [formRole, setFormRole] = useState<TAddressFormRoles>('add');
    const [address, setAddress] = useState<TAddress | null>(null);

    const checkHasAddresses = useCallback(async () => {
        try {
            const { data } = await customerSVC(locale.apiLocale).address.get();
            return data.data.length > 0;
        } catch (err) {
            return false;
        }
    }, []);

    useEffect(() => {
        if (isCustomerLoading) {
            setAddressView('loading');
            return;
        }

        if (customer?.email) {
            checkHasAddresses().then(hasAddresses => {
                if (hasAddresses) {
                    setAddressView('list');
                } else {
                    setAddressView('mapBrowse');
                }
            });
        } else {
            setClickedLocation(browsingAddress);
            updateSearchText(`${browsingAddress?.area} - ${browsingAddress?.city.name}`);
            setAddressView('mapBrowse');
        }
    }, [customer?.email, isCustomerLoading]);

    const switchView = (view: TAddressViews): void => {
        setAddressView(view);
    };

    const switchFormRole = (role: TAddressFormRoles): void => {
        setFormRole(role);
    };

    const handleEdit = (editAddress: TAddress): void => {
        setAddress(editAddress);
        handleMapClickDaily({
            lat: editAddress.lat / config.map.precision,
            lng: editAddress.lng / config.map.precision,
        });
        switchFormRole('edit');
        switchView('form');
    };

    const handleAdd = (): void => {
        removeClickedLocation();
        switchFormRole('add');
        switchView('mapBrowse');
    };

    const setBrowsingLocation = useCallback(async (lat, lng) => {
        try {
            const response = await customerSVC(locale.apiLocale).address.getDailyAreaFromLocation(lat, lng); // sets cookies
            changeCanEnterDaily(true);
            setBrowsingAddress(response.data);
            // addresses.toggleShowAddressModal(false);
            switchView('form');
        } catch (err) {
            // ARSALAN TODO - ERROR HANDLING PLEASE
            console.log(err);
        }
    }, []);

    const manualAddress = {
        area: _.get(clickedLocation, 'area'),
        city: _.get(clickedLocation, 'city'),
        lat: _.get(clickedLocation, 'lat'),
        lng: _.get(clickedLocation, 'lng'),
        thumbnail: _.get(clickedLocation, 'thumbnail'),
        streetAddress: '',
    };

    const getView = (): JSX.Element => {
        switch (addressView) {
            case 'loading':
                return (
                    <LoaderContainer>
                        <Loader color="brand" dotSize={20} />
                    </LoaderContainer>
                );
            case 'form':
                return (
                    <AddressForm
                        switchView={switchView}
                        formRole={formRole}
                        editAddressObj={formRole === 'add' ? manualAddress : address}
                    />
                );
            case 'mapBrowse': // for when a user is not signed in - but can select a general area to browse
                return (
                    <AccountLayout titleKey="" section="addresses" subtitleKey="" isLoading={false} error={null}>
                        <div>
                            <Title>{t('select-location')}</Title>
                            <Map />
                            <MapActions>
                                <div>
                                    <BlockButton
                                        onClick={() => setBrowsingLocation(clickedLocation.lat, clickedLocation.lng)}
                                        variant="primary"
                                        disabled={!clickedLocation}>
                                        {t('confirm-location')}
                                    </BlockButton>
                                </div>
                            </MapActions>
                        </div>
                    </AccountLayout>
                );
            default:
                return <AddressList onEdit={handleEdit} onAdd={handleAdd} />;
        }
    };

    return getView();
};

const LoaderContainer = styled.div`
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
`;

const Title = styled.h2`
    font-weight: bold;
    font-size: 1.25rem;
    margin-bottom: 16px;
`;

const MapActions = styled.div`
    margin-top: 16px;
    display: flex;

    div + div {
        ${({ theme: { left } }): string => `margin-${left}: 12px;`}
    }
`;

// --- Provide Toast Context
const AddressPage = (): JSX.Element => (
    <ToastContextProvider>
        <MapContextProvider showCollectionPoints={false}>
            <AddressMain />
        </MapContextProvider>
    </ToastContextProvider>
);

export default AddressPage;
